<template>
  <div>
    <h5 class="etapa-titulo">
      {{ modoEdicao ? "O que está configurado hoje" : "Confira antes de salvar" }}
    </h5>
    <p class="step-description">
      <template v-if="modoEdicao">
        Use o <strong>Editar</strong> de cada bloco para mudar só o que precisa.
      </template>
      <template v-else>
        Resumo do que será gravado.
      </template>
    </p>

    <CAlert v-if="validacao.erros.length" color="danger">
      <strong>
        <fa-icon icon="exclamation-triangle" class="mr-1" />
        Falta resolver {{ validacao.erros.length }} ponto(s) para salvar
      </strong>
      <ul class="mb-0 mt-1 pl-3">
        <li v-for="(erro, indice) in validacao.erros" :key="`erro-${indice}`">{{ erro }}</li>
      </ul>
      <div class="mt-2">
        <CButton
          v-for="etapa in etapasComErro"
          :key="`corrigir-${etapa.numero}`"
          size="sm"
          color="danger"
          variant="outline"
          class="mr-2"
          @click="$emit('ir-para-etapa', etapa.numero)"
        >
          <fa-icon icon="arrow-left" class="mr-1" />
          Corrigir "{{ etapa.titulo }}"
        </CButton>
      </div>
    </CAlert>

    <CAlert v-if="validacao.avisos.length" color="warning">
      <strong>
        <fa-icon icon="exclamation-triangle" class="mr-1" />
        Vale conferir — não impede salvar
      </strong>
      <ul class="mb-0 mt-1 pl-3">
        <li v-for="(aviso, indice) in validacao.avisos" :key="`aviso-${indice}`">{{ aviso }}</li>
      </ul>
    </CAlert>

    <CRow>
      <CCol col="12" md="6" class="mb-3">
        <CCard class="h-100 mb-0">
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <strong>Identificação</strong>
            <CButton size="sm" color="link" class="p-0" @click="$emit('ir-para-etapa', 1)">
              Editar
            </CButton>
          </CCardHeader>
          <CCardBody>
            <p class="resumo__destaque">{{ form.nome || "— sem nome —" }}</p>
            <p v-if="form.descricao" class="text-muted small">{{ form.descricao }}</p>
            <CBadge :color="form.ativo ? 'success' : 'secondary'">
              {{ form.ativo ? "Ativo" : "Inativo" }}
            </CBadge>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol col="12" md="6" class="mb-3">
        <CCard class="h-100 mb-0">
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <strong>Quais produtos entram</strong>
            <CButton size="sm" color="link" class="p-0" @click="$emit('ir-para-etapa', 2)">
              Editar
            </CButton>
          </CCardHeader>
          <CCardBody>
            <p v-if="!regras.length" class="text-muted small mb-0">
              Nenhuma condição definida.
            </p>
            <ul v-else class="list-unstyled mb-0">
              <li
                v-for="(regra, indice) in regras"
                :key="`resumo-regra-${indice}`"
                class="mb-1"
              >
                <CBadge :color="indice === 0 ? 'primary' : 'info'" class="mr-1">
                  {{ indice === 0 ? "SE" : "E" }}
                </CBadge>
                <span class="small">
                  <strong>{{ labelColuna(regra.colunaProduto) }}</strong>
                  é igual a
                  <strong>{{ labelValor(regra.colunaProduto, regra.valor) }}</strong>
                </span>
              </li>
            </ul>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol col="12" md="6" class="mb-3">
        <CCard class="h-100 mb-0">
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <strong>Como o cliente escolhe</strong>
            <CButton size="sm" color="link" class="p-0" @click="$emit('ir-para-etapa', 3)">
              Editar
            </CButton>
          </CCardHeader>
          <CCardBody>
            <p v-if="!campos.length" class="text-muted small mb-0">
              Nenhuma variação definida.
            </p>
            <ol v-else class="pl-3 mb-0">
              <li v-for="(campo, indice) in campos" :key="`resumo-campo-${indice}`">
                <strong class="small">{{ nomeExibido(campo) }}</strong>
                <small class="d-block text-muted">
                  {{ labelColuna(campo.colunaProduto) }} · ordem {{ rotuloOrdenacao(campo) }}
                </small>
              </li>
            </ol>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol col="12" md="6" class="mb-3">
        <CCard class="h-100 mb-0">
          <CCardHeader><strong>Alcance</strong></CCardHeader>
          <CCardBody>
            <p v-if="!diagnostico" class="text-muted small mb-0">
              Volte à etapa 2 para calcular o alcance.
            </p>
            <ul v-else class="list-unstyled mb-0 small">
              <li>
                <strong>{{ diagnostico.total_produtos }}</strong> produtos entram no grupo
              </li>
              <li>
                <strong>{{ diagnostico.total_grades }}</strong>
                {{ diagnostico.total_grades === 1 ? "grade" : "grades" }} se formam
              </li>
              <li>
                maior grade com <strong>{{ diagnostico.maior_grade }}</strong> SKUs
                <span class="text-muted">(limite {{ diagnostico.limite_permitido }})</span>
              </li>
              <li v-if="diagnostico.total_sem_agrupador" class="text-warning">
                <strong>{{ diagnostico.total_sem_agrupador }}</strong> sem agrupador ficarão sem grade
              </li>
            </ul>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CCard class="mb-0">
      <CCardHeader><strong>Pré-visualização</strong></CCardHeader>
      <CCardBody>
        <PreviewGrade :form="form" :colunasDisponiveis="colunasDisponiveis" />
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import PreviewGrade from "./PreviewGrade.vue";
import { ETAPAS } from "./etapas";

const MODOS = {
  natural: "automática",
  alfabetica: "alfabética",
  numerica: "numérica",
  cadastro: "de cadastro",
  manual: "manual",
};

export default {
  name: "EtapaRevisao",
  components: { PreviewGrade },
  props: {
    form: { type: Object, required: true },
    colunasDisponiveis: { type: Array, default: () => [] },
    diagnostico: { type: Object, default: null },
    /** Resultado de validarFormulario() — erros, avisos e porEtapa. */
    validacao: { type: Object, required: true },
    /** Na edição esta etapa é a porta de entrada, não a conferência final. */
    modoEdicao: { type: Boolean, default: false },
  },
  data() {
    return {
      // { coluna: { id: label } } — só para o resumo mostrar "Verso (5)" em vez de "5".
      labelsPorColuna: {},
    };
  },
  computed: {
    regras() {
      return this.form.regras || [];
    },
    campos() {
      return this.form.campos || [];
    },
    colunasMap() {
      return this.colunasDisponiveis.reduce((acumulador, opcao) => {
        acumulador[opcao.coluna] = opcao;
        return acumulador;
      }, {});
    },
    etapasComErro() {
      return ETAPAS.filter((etapa) => {
        const resultado = this.validacao.porEtapa[etapa.numero];
        return resultado && resultado.erros.length;
      });
    },
  },
  watch: {
    regras: {
      immediate: true,
      deep: true,
      handler() {
        this.carregarLabels();
      },
    },
  },
  methods: {
    labelColuna(coluna) {
      if (!coluna) return "—";
      const opcao = this.colunasMap[coluna];
      return opcao ? opcao.label : coluna;
    },
    // Mostra a descrição cadastrada; o código do ERP só aparece quando a coluna
    // não tem descrição para aquele valor.
    labelValor(coluna, valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      const mapa = this.labelsPorColuna[coluna];
      const label = mapa ? mapa[String(valor)] : null;
      return label || String(valor);
    },
    nomeExibido(campo) {
      const apelido = (campo.labelCustomizado || "").trim();
      return apelido || this.labelColuna(campo.colunaProduto);
    },
    rotuloOrdenacao(campo) {
      return MODOS[campo.ordenacao || "natural"] || "automática";
    },
    carregarLabels() {
      this.regras
        .map((regra) => regra.colunaProduto)
        .filter((coluna) => coluna && !this.labelsPorColuna[coluna])
        .forEach((coluna) => {
          const opcao = this.colunasMap[coluna];
          if (!opcao || !opcao.tem_valores_predef) return;

          this.$set(this.labelsPorColuna, coluna, {});
          get(`/admin/grupo-grade/valores-coluna?coluna=${encodeURIComponent(coluna)}`)
            .then((json) => {
              const mapa = {};
              (json.data || json || []).forEach((valor) => {
                mapa[String(valor.id)] = valor.label;
              });
              this.$set(this.labelsPorColuna, coluna, mapa);
            })
            .catch(() => {});
        });
    },
  },
};
</script>

<style scoped>
.etapa-titulo {
  font-weight: 600;
  color: #1d2939;
}

.step-description {
  color: #667085;
  font-size: 13px;
  margin-bottom: 20px;
  max-width: 72ch;
}

.resumo__destaque {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}
</style>
